/** Probate 172 — Stream F */
import DocumentUploadSection from '../../settlement/DocumentUploadSection';
import {
  probateFieldLabelClass,
  probateInputClass,
  ProbateBackLink,
  ProbateChecklistCard,
  ProbateChecklistProgress,
  ProbateContextBlock,
} from './probateStepUi';
import { useProbateDocuments } from './useProbateDocuments';
import { useProbateStep } from './useProbateStep';

const STEP_ID = 'prob-2';
const FORM_FIELDS =
  'courtName,caseNumber,decedentFullName,decedentDod,petitionerFullName,petitionerRelationship';

export default function SFrame172() {
  const {
    estateId,
    isArchived,
    error,
    stepDef,
    g,
    checklistDefs,
    checklistCompleted,
    checklistTotal,
    checkedIds,
    toggleItem,
    formValues,
    setField,
    touchActivity,
  } = useProbateStep(STEP_ID, FORM_FIELDS);

  const ro = !!isArchived;
  const { documents, upload, remove } = useProbateDocuments(estateId, STEP_ID, ro);

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <ProbateBackLink estateId={estateId} />
      {error ? (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      ) : null}
      <h1 className="text-2xl font-semibold text-stone-800 mb-2">{stepDef?.title}</h1>
      {g?.point && g?.goal ? <ProbateContextBlock point={g.point} goal={g.goal} /> : null}
      <ProbateChecklistProgress completed={checklistCompleted} total={checklistTotal} />

      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-4 space-y-4">
        <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">Inputs</h2>
        <div>
          <label className={probateFieldLabelClass} htmlFor="courtName">Court name</label>
          <input id="courtName" className={probateInputClass} disabled={ro} value={formValues.courtName ?? ''}
            onChange={(e) => setField('courtName', e.target.value)} placeholder="Probate division / county clerk" />
        </div>
        <div>
          <label className={probateFieldLabelClass} htmlFor="caseNumber">Case number (once assigned)</label>
          <input id="caseNumber" className={probateInputClass} disabled={ro} value={formValues.caseNumber ?? ''}
            onChange={(e) => setField('caseNumber', e.target.value)} placeholder="e.g. XX-YYY-YYYY" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={probateFieldLabelClass} htmlFor="decedentFullName">Decedent information</label>
            <input id="decedentFullName" className={probateInputClass} disabled={ro}
              placeholder="Legal name"
              value={formValues.decedentFullName ?? ''}
              onChange={(e) => setField('decedentFullName', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={probateFieldLabelClass} htmlFor="decedentDod">Date of death (if known)</label>
            <input id="decedentDod" type="date" className={probateInputClass} disabled={ro}
              value={formValues.decedentDod ?? ''}
              onChange={(e) => setField('decedentDod', e.target.value)} />
          </div>
        </div>
        <div>
          <label className={probateFieldLabelClass} htmlFor="petitionerFullName">Petitioner / personal representative candidate</label>
          <input id="petitionerFullName" className={probateInputClass} disabled={ro}
            value={formValues.petitionerFullName ?? ''}
            onChange={(e) => setField('petitionerFullName', e.target.value)} />
        </div>
        <div>
          <label className={probateFieldLabelClass} htmlFor="petitionerRelationship">Relationship to decedent</label>
          <input id="petitionerRelationship" className={probateInputClass} disabled={ro}
            value={formValues.petitionerRelationship ?? ''}
            onChange={(e) => setField('petitionerRelationship', e.target.value)} />
        </div>
      </div>

      <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wide mb-2">Checklist</h2>
      <ProbateChecklistCard items={checklistDefs} checkedIds={checkedIds} toggleItem={toggleItem} disabled={ro} />

      <div className="bg-white rounded-xl border border-stone-200 p-5">
        <DocumentUploadSection
          documents={documents}
          onUpload={async (f) => { await touchActivity(); await upload(f); }}
          onDelete={ro ? undefined : (id) => remove(id)}
          disabled={ro}
        />
        <p className="text-xs text-stone-500 mt-3">Upload petition draft, death certificate, and filing confirmation.</p>
      </div>
    </div>
  );
}
